export type HubPageWidthTier = "narrow" | "default" | "wide";
export type HubPageHeightMode = "document" | "workspace" | "kiosk";

export type HubPageLayoutPolicy = {
  widthTier: HubPageWidthTier;
  heightMode: HubPageHeightMode;
};

const BOM_WORKBENCH_PATHS = ["/bom/set-bom-rules", "/bom/approve-bom-rules", "/bom/test-revision"];

const WIDE_EXACT_PATHS = new Set(["/erp", "/personal/home"]);

const WIDE_PATH_PREFIXES = [
  "/erp/erpnext-sync",
  "/erp/findings",
  "/erp/entity",
  "/erp/data-control-registry",
  "/mrp/forecast",
  "/mrp/ghost-build-orders",
  "/mrp/clamp-forecast",
  "/mrp/planning",
  "/mrp/tla-planning",
  "/mrp/tla-planning/detail",
  "/mrp/build-plan",
  ...BOM_WORKBENCH_PATHS,
  "/clamp-qc/jobs",
  "/clamp-qc/work-orders",
  "/clamp-qc/reporting",
  "/manufacturing/sim",
  "/manufacturing/laser-mark",
  "/manufacturing-management/work-orders",
  "/manufacturing-management/batch-details",
  "/manufacturing-management/suntronic-box-labels",
  "/manufacturing-management/edit-batch",
  "/manufacturing-management/production-calendar",
  "/manufacturing-management/production-capacity",
  "/manufacturing-management/forward-demand",
  "/manufacturing-management/reporting",
  "/manufacturing-management/potting-po",
  "/manufacturing-management/clamp-qc-credit",
  "/manufacturing-management/safety-reports",
  "/manufacturing-management/transfers",
  "/manufacturing-management/wireless-issue",
  "/shipping/pre-pack/transfers",
  "/shipping/inbound",
  "/shipping/arrivals",
  "/shipping/holding",
  "/shipping/to-stock",
  "/shipping/shipment-details",
  "/shipping/gosi-details",
  "/mrp/purchase-invoices",
  "/admin/module-access",
  "/accounting/cash-flow",
  "/accounting/inventory-over-time",
  "/accounting/eom-reconciliation",
  "/accounting/suntronic-invoices",
  "/accounting/receipt-discrepancy",
  "/quality/corrective-actions",
  "/quality/cases",
  "/quality/events",
  "/quality/rmas",
  "/quality/qc-incidents",
  "/documents/drawing-coverage",
  "/inside-sales",
  "/people/training",
  "/people/permissions",
];

export const HUB_PAGE_SHELL_WIDTH_BY_TIER: Record<HubPageWidthTier, string> = {
  narrow: "max-w-[min(100%,960px)] w-full px-4 sm:px-6",
  default: "max-w-[min(100%,1600px)] w-full px-3 sm:px-4 lg:px-6 2xl:px-10",
  wide: "max-w-[min(100%,2400px)] w-full px-3 sm:px-4 lg:px-6 2xl:px-10",
};

const WORKSPACE_PATH_PATTERNS: RegExp[] = [
  /^\/quality\/settings\/failure-modes$/,
  /^\/quality\/settings\/operator-scripts$/,
  /^\/quality\/settings\/tier-1-scripts$/,
  /^\/accounting\/ap\/new$/,
  /^\/accounting\/ap\/[^/]+\/edit$/,
  /^\/accounting\/suntronic-invoices\/[^/]+$/,
  /^\/inside-sales\/sales-orders\/[^/]+$/,
  /^\/inside-sales\/reports$/,
  /^\/inside-sales\/settings$/,
  /^\/inside-sales\/quotes$/,
  /^\/inside-sales\/wireless-portal-accounts$/,
];

const KIOSK_PATH_PATTERNS: RegExp[] = [
  /^\/manufacturing\/kitting\/pick$/,
  /^\/shipping\/receive\/[^/]+\/[^/]+$/,
  /^\/shipping\/pre-pack\/transfers\/[^/]+$/,
];

const SALES_ORDER_EDITOR_EXCLUDED_PAGES = new Set(["approvals", "settings", "rep-assignment"]);

export function resolveHubPageWidthTier(pathname: string): HubPageWidthTier {
  if (
    pathname === "/warehouse/carton-adjust"
    || pathname.startsWith("/i/")
    || pathname.startsWith("/a/")
    || pathname.startsWith("/personal/reimbursements")
    || pathname.startsWith("/personal/dfm-change-review")
    || pathname.startsWith("/inv/")
  ) {
    return "narrow";
  }
  if (
    WIDE_EXACT_PATHS.has(pathname) ||
    WIDE_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
  ) {
    return "wide";
  }
  return "default";
}

export function hubPageShellWidthClassName(tier: HubPageWidthTier): string {
  return HUB_PAGE_SHELL_WIDTH_BY_TIER[tier];
}

export function isSalesOrderEditorPath(pathname: string): boolean {
  const prefix = "/inside-sales/sales-orders/";
  if (!pathname.startsWith(prefix)) {
    return false;
  }
  const page = pathname.slice(prefix.length);
  return page !== "" && !page.includes("/") && !SALES_ORDER_EDITOR_EXCLUDED_PAGES.has(page);
}

export function isManufacturerInvoiceWorkPath(pathname: string): boolean {
  return /^\/accounting\/suntronic-invoices\/[^/]+$/.test(pathname);
}

export function isInsideSalesReportsPath(pathname: string): boolean {
  return pathname === "/inside-sales/reports";
}

export function isKittingPickPath(pathname: string): boolean {
  return pathname === "/manufacturing/kitting/pick";
}

export function isQualityPrintDocumentPath(pathname: string): boolean {
  return (
    /\/quality\/cases\/[^/]+(?:\/investigations\/[^/]+)?\/traveler$/.test(pathname) ||
    /\/quality\/rmas\/[^/]+\/document$/.test(pathname)
  );
}

export function resolveHubPageHeightMode(pathname: string): HubPageHeightMode {
  if (WORKSPACE_PATH_PATTERNS.some((pattern) => pattern.test(pathname))) {
    if (pathname.startsWith("/inside-sales/sales-orders/") && !isSalesOrderEditorPath(pathname)) {
      return "document";
    }
    return "workspace";
  }
  if (KIOSK_PATH_PATTERNS.some((pattern) => pattern.test(pathname))) {
    return "kiosk";
  }
  return "document";
}

export function resolveHubPageLayoutPolicy(pathname: string): HubPageLayoutPolicy {
  return {
    widthTier: resolveHubPageWidthTier(pathname),
    heightMode: resolveHubPageHeightMode(pathname),
  };
}

export function isHubWorkspaceHeightMode(heightMode: HubPageHeightMode): boolean {
  return heightMode === "workspace";
}
